import _ from 'underscore';
import classNames from 'classnames';
import React, { useCallback, useState, useMemo, useContext } from 'react';
import {
  Button,
  Divider,
  Field,
  RadioGroup,
  Radio,
  Label,
  Input,
  Dropdown,
  Option,
  Text,
  makeStyles,
  useId,
} from '@fluentui/react-components';
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerFooter,
  Drawer,
} from '@fluentui/react-drawer';
import { Dismiss24Regular } from '@fluentui/react-icons';

import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';

import { CampaignAdgroupSelector } from './campaign-adgroup-selector';

interface IAddKeywordPanelProps {
  campaign: {
    Id: string;
    Name: string;
  };
  adGroup: {
    Id: string;
    Name: string;
  };
  keywordText: string;
  isNegative: boolean;
  onClose: (dataSaved? : boolean) => void;
  i18n: any;
  jsdata: any;
  cid: number,
  aid: number,
  campaignId?: number,
}

type TRadioGroupValue = 'campaign' | 'adgroup';

const useStyles = makeStyles({
  divider: {
    maxHeight: '1px',
  },
  keywordText: {
    width: '100%',
  },
  dropdown: {
    width: '100%',
  },
  helpLink: {
    marginTop: '8px',
  },
});

export function AddKeywordDrawer({
  campaign,
  adGroup,
  keywordText,
  isNegative,
  onClose,
  i18n,
  jsdata,
  cid,
  aid,
  campaignId,
}: IAddKeywordPanelProps) {
  const classes = useStyles();
  const inputId = useId('input');
  const dropdownId = useId('dropdown');
  const campaignAdGroupSelectorId = useId('campaign-adgroup-selector');
  const [keywordValue, setKeywordValue] = useState(keywordText);
  const options = useMemo(() => (isNegative ? [{
    key: 'Exact',
    text: i18n.getString(_TL_('Exact')),
  }, {
    key: 'Phrase',
    text: i18n.getString(_TL_('Phrase')),
  }] : [{
    key: 'Exact',
    text: i18n.getString(_TL_('Exact')),
  }, {
    key: 'Phrase',
    text: i18n.getString(_TL_('Phrase')),
  }, {
    key: 'Broad',
    text: i18n.getString(_TL_('Broad')),
  }]), [i18n, isNegative]);
  const [selectedOption, setSelectedOption] = useState(options[0].key);
  const [selectedOptionText, setSelectedOptionText] = useState(options[0].text);
  const [radioGroupValue, setRadioGroupValue] = useState<TRadioGroupValue>('adgroup');
  const isAdGroupLevel = radioGroupValue === 'adgroup';
  const [isSelectorDataLoaded, setIsSelectorDataLoaded] = useState(false);

  const title = isNegative ? i18n.getString(_TL_('Add as negative keyword')) : i18n.getString(_TL_('Add as keyword'));
  const alertHelper = useMemo(() => new AlertHelper({ i18n }), [i18n]);

  const instrumentation = useContext(InstrumentationContext) as any;
  const { activity } = useMemo(() => instrumentation.createScenarioAndActivity({
    scenarioName: 'Fluent2OverviewPage',
    activityName: 'SearchTerm',
  }), [instrumentation]);

  const [selectedCampaign, setSelectedCampaign] = useState(campaign);
  const [selectedAdGroup, setSelectedAdGroup] = useState(adGroup);
  const selectorTitle = isAdGroupLevel ? i18n.getString(_TL_('Select ad group')) : i18n.getString(_TL_('Select campaign'));

  const onSavedClose = useCallback(() => { onClose(true); }, [onClose]);
  const onCancelClose = useCallback(() => { onClose(false); }, [onClose]);

  const onSaveClicked = useCallback(() => {
    if (isNegative) {
      const negativeKeyword = {
        NegativeKeywords: [{
          Text: keywordValue,
          EntityType: isAdGroupLevel ? 'AdGroup' : 'Campaign',
          CampaignId: selectedCampaign.Id,
          AdGroupId: selectedAdGroup.Id,
          MatchType: selectedOption,
        }],
      };
      const campaignEntity = edmCampaign
        .Customers.$withKey(cid)
        .Accounts.$withKey(aid)
        .Campaigns.$withKey(selectedCampaign.Id);
      let entitySet = campaignEntity;

      if (isAdGroupLevel) {
        entitySet = campaignEntity.AdGroups.$withKey(selectedAdGroup.Id);
      }

      jsdata.NegativeKeyword.create(negativeKeyword, {
        entitySet: entitySet.NegativeKeywords['Default.PostNegativeKeywords'],
      })
        .then(() => {
          onSavedClose();
          alertHelper.showSuccess(i18n.getString(_TL_('Keyword added.')));
        })
        .catch((error) => {
          activity.error(JSON.stringify(error));
          alertHelper.showError(i18n.getString(_TL_('We’ve encountered an error and can’t continue. Please contact support for further assistance.')));
        });
    } else {
      const keywordData = {
        Campaign: { Id: selectedCampaign.Id },
        Name: selectedAdGroup.Name,
        Id: selectedAdGroup.Id,
        Keywords: [{
          Text: keywordValue,
          MatchType: selectedOption,
          Bid: {},
          AdGroup: { Id: selectedAdGroup.Id },
        }],
      };

      jsdata.AdGroup
        .update(keywordData.Id, keywordData, { adapter: 'bulk' })
        .then(() => {
          onSavedClose();
          alertHelper.showSuccess(i18n.getString(_TL_('Keyword added.')));
        })
        .catch((error) => {
          activity.error(JSON.stringify(error));
          alertHelper.showError(i18n.getString(_TL_('We’ve encountered an error and can’t continue. Please contact support for further assistance.')));
        });
    }
  }, [activity, aid, alertHelper, cid, i18n, isAdGroupLevel, isNegative,
    jsdata, keywordValue, onSavedClose, selectedAdGroup, selectedCampaign, selectedOption]);

  const onRadioGroupChange = useCallback((_, data) => {
    setRadioGroupValue(data.value);
  }, []);

  const onKeywordChange = useCallback((ev, data) => {
    setKeywordValue(data.value.trim());
  }, []);

  const onOptionSelect = useCallback((ev, data) => {
    setSelectedOption(data.optionValue);
    setSelectedOptionText(data.optionText);
  }, []);

  const onSelectorDataLoaded = useCallback(() => {
    setIsSelectorDataLoaded(true);
  }, []);

  const onSelectorSelectionChange = useCallback(({
    selectedCampaign: newSelectedCampaign,
    selectedAdGroup: newSelectedAdGroup,
  }) => {
    if (isAdGroupLevel) {
      setSelectedCampaign(newSelectedCampaign);
      setSelectedAdGroup(newSelectedAdGroup);
    } else {
      setSelectedCampaign(newSelectedCampaign);
    }
  }, [isAdGroupLevel]);

  return (
    <Drawer
      className="add-keyword-drawer"
      open
      position="end"
      onOpenChange={onCancelClose}
      size="medium"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              className="close"
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={onCancelClose}
            />
          }
        >
          {title}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {isNegative &&
          <Field label={i18n.getString(_TL_('Add negative keywords to'))}>
            <RadioGroup value={radioGroupValue} onChange={onRadioGroupChange}>
              <Radio value="campaign" label={i18n.getString(_TL_('Campaign level'))} />
              <Radio value="adgroup" label={i18n.getString(_TL_('Ad group level'))} />
            </RadioGroup>
          </Field>
        }
        <Label htmlFor={campaignAdGroupSelectorId}>
          {selectorTitle}
        </Label>
        <CampaignAdgroupSelector
          key={isAdGroupLevel ? 'adgroup' : 'campaign'}
          cid={cid}
          aid={aid}
          campaignId={campaignId}
          jsdata={jsdata}
          isAdGroupLevel={isAdGroupLevel}
          defalutSelectedCampaign={campaign}
          defalutSelectedAdGroup={adGroup}
          onInitFinished={onSelectorDataLoaded}
          onSelectionChange={onSelectorSelectionChange}
        />
        <Label htmlFor={inputId} required>
          {i18n.getString(_TL_('Keyword'))}
        </Label>
        <Input
          value={keywordValue}
          onChange={onKeywordChange}
          id={inputId}
          className={classNames('keyword-text-input', classes.keywordText)}
        />
        <Label htmlFor={dropdownId}>{i18n.getString(_TL_('Type'))}</Label>
        <Dropdown
          aria-labelledby={dropdownId}
          selectedOptions={[selectedOption]}
          value={selectedOptionText}
          onOptionSelect={onOptionSelect}
          className={classNames('type-dropdown', classes.dropdown)}
        >
          {options.map(option => (
            <Option key={option.key} value={option.key} text={option.text}>{option.text}</Option>
          ))}
        </Dropdown>
        <div className={classes.helpLink}>
          <Text>{i18n.getString(_TL_('Not sure which match type to use?'))}</Text>
          <LearnMoreLink
            ariaLabel={i18n.getString(_TL_('Not sure which match type to use?'))}
            linkText={i18n.getString(_TL_('Learn more'))}
            topic="app51021"
          />
        </div>
      </DrawerBody>
      <Divider className={classes.divider} />
      <DrawerFooter>
        <Button disabled={!isSelectorDataLoaded} className="save" appearance="primary" onClick={onSaveClicked}>{i18n.getString(_TL_('Apply'))}</Button>
        <Button className="cancel" onClick={onCancelClose}>{i18n.getString(_TL_('Cancel'))}</Button>
      </DrawerFooter>
    </Drawer>
  );
}

AddKeywordDrawer.defaultProps = {
  campaignId: null,
};
